import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import heic2any from "heic2any";
import Compressor from "compressorjs";
import update from "immutability-helper";
import { FileUploadButton } from "@app/components/atoms";
import { TYPE_FILE_UPLOAD } from "@app/constants";
import { setErrorsUploadFile, setIsLoading } from "@app/store";
import { iOSversion, isMobileDevice, isValidMediaFile } from "@app/helpers";
import { format } from "date-fns";
import { MediaUploadMenu } from "./MediaUploadMenu";

const VIDEO_DURATION = 120;
const IMAGE_SIZE = 10485760;
const SUPPORT_EXTENSION_MAP = {
  video: "^.*.(mp4|mov)$",
  image: "^.*.(png|jpg|jpeg|gif|heic)$",
};
const CONVERT_TYPE = ".jpeg";
const MIME_TYPE = "image/jpeg";
const THUMBNAIL_CONVERT_TYPE = ".jpg";
const THUMBNAIL_MIME_TYPE = "image/jpg";

const validateExtension = (extensionRegExp: string, text: string) => {
  const regExp = new RegExp(extensionRegExp, "i");
  return regExp.test(text);
};

export const MediaUpload = ({
  medias,
  setMedias,
  id = "",
  typeFile = TYPE_FILE_UPLOAD.imageAndVideo,
  uploadDescription,
  onPreviewMedia,
  errMessage = "",
  setFileCount,
  setHasUploadError,
  maxFiles = 2,
  fileUploadLimit,
  showMaxUploadLimit = false,
}: any) => {
  const dispatch = useDispatch();
  const [media, setMedia] = useState<
    File[] | (File & { duration: number })[] | any[]
  >([]);
  const [uploadErrMessage, setUploadErrMessage] = useState("");
  const [uploadInfoMessage, setUploadInfoMessage] = useState("");
  const [hasBlockingUploadError, setHasBlockingUploadError] = useState(false);
  const [maxSelectableFiles, setMaxSelectableFiles] = useState(maxFiles);
  const [uploadMediaDescription, setUploadMediaDescription] =
    useState(uploadDescription);
  const { t } = useTranslation();
  const isInternalMediaUpdateRef = useRef(false);

  const notifyMediasChange = (nextMedias: File[]) => {
    isInternalMediaUpdateRef.current = true;
    setMedias(nextMedias);
  };

  useEffect(() => {
    const currentMediaCount = medias?.length || 0;
    const availableForType = maxFiles - currentMediaCount;
    const totalAvailable =
      fileUploadLimit !== undefined
        ? 50 - (fileUploadLimit + medias.length) // Cap at 50 total
        : availableForType;
    const maxSelectable = Math.max(
      0,
      Math.min(availableForType, totalAvailable),
    );
    setMaxSelectableFiles(maxSelectable);
    // Update description to include max uploadable files if there's showMaxUploadLimit prop
    // And show a different message if maxSelectable is already 0
    if (maxSelectable === 0) {
      setUploadMediaDescription([
        `${t("maxFilesReached", {
          max: maxSelectable,
        })}`,
      ]);
    } else if (showMaxUploadLimit) {
      setUploadMediaDescription([
        uploadDescription[0],
        `<span class="fs-12">${t("dialogReport.maxFilesInfo", {
          max: maxSelectable,
        })}</span>`,
        ...uploadDescription.slice(1),
      ]);
    } else {
      setUploadMediaDescription(uploadDescription);
    }
  }, [medias.length, fileUploadLimit, maxFiles, uploadDescription, t]);

  const setVideo = (
    e: React.ChangeEvent<HTMLInputElement>,
    action: string,
    index: number | undefined,
  ) => {
    const validateVideo = validateExtension(
      SUPPORT_EXTENSION_MAP.video,
      e.target.value,
    );

    if (!validateVideo) {
      setUploadErrMessage(t("messages.wrongVideo"));
      setHasBlockingUploadError(true);
      dispatch(setIsLoading(false));
      e.target.value = "";
      return;
    }

    const videoElem = document.createElement("video");
    const objectUrl = URL.createObjectURL(e.target.files![0]);

    videoElem.src = objectUrl;
    videoElem.preload = "metadata";

    videoElem.onloadedmetadata = async () => {
      const duration = videoElem.duration;

      if (duration >= VIDEO_DURATION) {
        setUploadErrMessage(t("messages.errOver2min"));
        setHasBlockingUploadError(true);
        dispatch(setIsLoading(false));
        e.target.value = "";
        URL.revokeObjectURL(objectUrl);
        return;
      }
      const videoFile = e.target.files![0] as File & { duration: number };
      videoFile.duration = duration;

      const convertThumbnailFile = (videoThumpUrl: string) => {
        const file = new File(
          [b64toBlob(videoThumpUrl)],
          `${videoFile.name.slice(
            0,
            videoFile.name.length - 4,
          )}${CONVERT_TYPE}`,
          {
            type: MIME_TYPE,
          },
        );
        let quality = 0.6;
        if (
          isMobileDevice &&
          iOSversion() > 0 &&
          iOSversion() < 16 &&
          file.size < IMAGE_SIZE / 5
        ) {
          quality = 1;
        }
        new Compressor(file, {
          mimeType: THUMBNAIL_MIME_TYPE,
          quality,
          resize: "cover",
          success(result) {
            const lastDotIndex = videoFile.name.lastIndexOf(".");
            const file = new File(
              [result],
              `${videoFile.name.slice(
                0,
                lastDotIndex,
              )}${THUMBNAIL_CONVERT_TYPE}`,
              {
                type: THUMBNAIL_MIME_TYPE,
              },
            );
            const formatVideoFile = Object.defineProperties(videoFile, {
              thumbnail: {
                value: file,
                configurable: true,
              },
              poster: {
                value: videoThumpUrl,
                configurable: true,
              },
            });
            if (action === "replace" && typeof index === "number") {
              setMedia((prev) => {
                const newMedia = update(prev, {
                  [index]: { $set: formatVideoFile },
                });
                notifyMediasChange(newMedia);
                return newMedia;
              });
            } else {
              setMedia((prev) => {
                const newMedia = [...prev, formatVideoFile];
                notifyMediasChange(newMedia);
                return newMedia;
              });
            }
            dispatch(setIsLoading(false));
          },
          error() {
            setUploadErrMessage(t("messages.errConmpressImg"));
            setHasBlockingUploadError(true);
            dispatch(setIsLoading(false));
            URL.revokeObjectURL(objectUrl);
          },
        });
      };
      const videoThumpUrl = await getThumbnailForVideo(objectUrl);
      convertThumbnailFile(videoThumpUrl);
      e.target.value = "";
    };
  };

  const getThumbnailForVideo = async (videoUrl: string) => {
    const video = document.createElement("video");
    const canvas = document.createElement("canvas");
    video.style.display = "none";
    canvas.style.display = "none";

    await new Promise<void>((resolve) => {
      video.preload = "metadata";
      video.addEventListener("loadedmetadata", () => {
        video.width = video.videoWidth;
        video.height = video.videoHeight;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        video.play();
      });
      video.addEventListener("timeupdate", () => resolve());
      video.src = videoUrl;
      video.muted = true;
      video.playsInline = true;
    });

    canvas
      .getContext("2d")
      ?.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
    const imageUrl = canvas.toDataURL("image/jpeg");
    return imageUrl;
  };

  const b64toBlob = (dataURI: string) => {
    var byteString = atob(dataURI.split(",")[1]);
    var ab = new ArrayBuffer(byteString.length);
    var ia = new Uint8Array(ab);
    for (var i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: "image/jpeg" });
  };

  const setImage = async (
    e: React.ChangeEvent<HTMLInputElement>,
    action: string,
    index: number | undefined,
  ) => {
    const validateImage = validateExtension(
      SUPPORT_EXTENSION_MAP.image,
      e.target.value,
    );

    if (!validateImage) {
      setUploadErrMessage(t("messages.wrongImage"));
      setHasBlockingUploadError(true);
      dispatch(setIsLoading(false));
      e.target.value = "";
      return;
    }
    const fileTarget = e.target.files![0];
    const isValidFile = await isValidMediaFile(fileTarget);

    if (!isValidFile) {
      setUploadErrMessage(t("messages.corruptFileType"));
      setHasBlockingUploadError(true);
      dispatch(setIsLoading(false));
      e.target.value = "";
      return;
    }

    const imageSize = fileTarget.size;

    if (imageSize >= IMAGE_SIZE) {
      setUploadErrMessage(t("messages.errSizeImage"));
      setHasBlockingUploadError(true);
      dispatch(setIsLoading(false));
      e.target.value = "";
      return;
    }

    const convertFile = (file: File) => {
      let quality = 0.6;
      if (
        isMobileDevice &&
        iOSversion() > 0 &&
        iOSversion() < 16 &&
        imageSize < IMAGE_SIZE / 5
      ) {
        quality = 1;
      }
      new Compressor(file, {
        mimeType: MIME_TYPE,
        quality,
        resize: "cover",
        success(result) {
          handleUploadStore(file, result, action, index);
          dispatch(setIsLoading(false));
        },
        error() {
          setUploadErrMessage(t("messages.errConmpressImg"));
          setHasBlockingUploadError(true);
          dispatch(setIsLoading(false));
        },
      });
    };

    if (fileTarget.type === "image/heic") {
      heic2any({
        blob: fileTarget,
        toType: MIME_TYPE,
      }).then((result) => {
        const file = new File(
          [result as Blob],
          `${fileTarget.name.slice(
            0,
            fileTarget.name.length - 3,
          )}${CONVERT_TYPE}`,
          {
            type: MIME_TYPE,
          },
        );
        convertFile(file);
      });
    } else {
      convertFile(fileTarget);
    }

    e.target.value = "";
  };

  const handleUploadStore = (
    fileTarget: File,
    result: BlobPart,
    action: string,
    index: number | undefined,
  ) => {
    const originalLastModified = fileTarget.lastModified;
    const lastDotIndex = fileTarget.name.lastIndexOf(".");
    const file = new File(
      [result],
      `${fileTarget.name.slice(0, lastDotIndex)}${CONVERT_TYPE}`,
      {
        type: MIME_TYPE,
      },
    );

    Object.defineProperty(file, "lastModified", {
      value: originalLastModified,
      writable: false,
    });

    Object.defineProperty(file, "lastModifiedFormatted", {
      value: format(new Date(originalLastModified), "yyyy-MM-dd HH:mm"),
      writable: false,
    });

    if (action === "replace" && typeof index === "number") {
      setMedia((prev) => {
        const newMedia = update(prev, { [index]: { $set: file } });
        notifyMediasChange(newMedia);
        return newMedia;
      });
    } else {
      setMedia((prev) => {
        const newMedia = [...prev, file];
        notifyMediasChange(newMedia);
        return newMedia;
      });
    }
  };

  const handleUploadFile = (
    e: React.ChangeEvent<HTMLInputElement>,
    action: string,
    index?: number,
  ) => {
    e.preventDefault();
    if (uploadErrMessage) {
      setUploadErrMessage("");
      setHasBlockingUploadError(false);
    }
    if (uploadInfoMessage) setUploadInfoMessage("");

    if (!!e.target.files?.length) {
      let files = Array.from(e.target.files);

      // Enforce limit: discard excess files (informational only — does not block submit)
      if (files.length > maxSelectableFiles) {
        files = files.slice(0, maxSelectableFiles);
        setUploadInfoMessage(
          t("maxFilesExceeded", {
            max:
              fileUploadLimit && maxSelectableFiles < maxFiles
                ? 50
                : maxSelectableFiles,
          }),
        );
        e.target.value = "";
      }

      dispatch(setIsLoading(true));

      const existingFiles = [...media];
      const allFiles = [...existingFiles, ...files];
      setFileCount?.(allFiles.length);

      files.forEach(async (file) => {
        const isVideo = file.type.includes("video");
        const isImage = file.type.includes("image");
        const fileExtension = file.name.split(".").pop()?.toLowerCase();
        const isHeic = file.type === "image/heic" || fileExtension === "heic";

        if (isHeic) {
          try {
            const convertedBlob = await heic2any({
              blob: file,
              toType: "image/jpeg",
            });

            const convertedFile = new File(
              [convertedBlob as Blob],
              `${file.name.replace(/\.heic$/i, ".jpeg")}`,
              {
                type: "image/jpeg",
                lastModified: file.lastModified,
              },
            );

            Object.defineProperty(convertedFile, "lastModifiedFormatted", {
              value: format(new Date(file.lastModified), "yyyy-MM-dd HH:mm"),
              writable: false,
            });

            const fileEvent = {
              target: {
                files: [convertedFile],
                value: convertedFile.name,
              },
            };

            setImage(fileEvent as any, action, index);
          } catch (err) {
            console.error("HEIC conversion failed:", err);
            setUploadErrMessage(t("messages.wrongFileType"));
            setHasBlockingUploadError(true);
            dispatch(setIsLoading(false));
          }
          return;
        }

        if (isVideo || isImage) {
          if (isVideo && typeFile !== TYPE_FILE_UPLOAD.image) {
            const fileEvent = { target: { files: [file], value: file.name } };
            setVideo(fileEvent as any, action, index);
          } else {
            const fileEvent = { target: { files: [file], value: file.name } };
            setImage(fileEvent as any, action, index);
          }
        } else {
          setUploadErrMessage(t("messages.wrongFileType"));
          setHasBlockingUploadError(true);
          dispatch(setIsLoading(false));
        }
      });

      e.target.value = "";
    }
  };

  const handleDeleteFile = (index: number) => {
    const newImages = [...media];
    newImages.splice(index, 1);
    setMedia(newImages);
    notifyMediasChange(newImages);
    setUploadErrMessage("");
    setUploadInfoMessage("");
    setHasBlockingUploadError(false);

    if (setFileCount) {
      setFileCount((prevCount: number) => {
        const newCount = Math.max(prevCount - 1, 0);

        return newCount;
      });
    }
  };

  useEffect(() => {
    dispatch(setErrorsUploadFile(uploadErrMessage));
    setHasUploadError?.(hasBlockingUploadError);
  }, [uploadErrMessage, hasBlockingUploadError, dispatch, setHasUploadError]);

  useEffect(() => {
    if (isInternalMediaUpdateRef.current) {
      isInternalMediaUpdateRef.current = false;
      return;
    }
    setMedia(medias || []);
  }, [medias]);

  return (
    <div
      className="flex-direction-column wrapper-upload-file"
      style={{ minHeight: "auto", width: "100%" }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          marginBottom: media?.length ? "5px" : "0",
        }}
      >
        {!media?.length &&
          uploadMediaDescription?.map((description: string, index: number) => (
            <p
              key={index}
              className="txt-gray-1 fs-14 m-0 mt-8"
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            ></p>
          ))}
        {(media?.length < maxFiles || media === null) &&
          (fileUploadLimit === undefined ||
            fileUploadLimit + medias.length < 50) && (
            <FileUploadButton
              className={`upload-button btn-secondary ${
                !media?.length && "mt-8"
              }`}
              htmlFor={`upload-file-${id}`}
            >
              {t("uploadFile")}
              <input
                type="file"
                accept={typeFile}
                className="btn-secondary"
                id={`upload-file-${id}`}
                hidden
                multiple
                onChange={(e) => handleUploadFile(e, "add")}
              />
            </FileUploadButton>
          )}
      </div>

      <div
        className={`wrapper-upload flex-centered ${
          media?.length ? "justify-content-start" : ""
        }`}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "12px",
          width: "100%",
        }}
      >
        {media &&
          media.map((media, index) => (
            <MediaUploadMenu
              key={`${id}_${media.name}_${media?.lastModified}_${index}`}
              media={media}
              index={index}
              uploadId={id}
              onUploadFile={handleUploadFile}
              deleteFile={handleDeleteFile}
              typeFile={typeFile}
              onPreviewMedia={(url) => {
                if (Array.isArray(url)) {
                  url.forEach((u) =>
                    window.open(u, "_blank", "noopener,noreferrer"),
                  );
                } else {
                  window.open(url, "_blank", "noopener,noreferrer");
                }
              }}
            />
          ))}
      </div>

      {uploadInfoMessage && (
        <p
          className={`${
            media?.length ? "text-left" : "text-center"
          } upload-err-message fs-12`}
        >
          {uploadInfoMessage}
        </p>
      )}

      {uploadErrMessage && (
        <p
          className={`${
            media?.length ? "text-left" : "text-center"
          } upload-err-message fs-12`}
        >
          {uploadErrMessage}
        </p>
      )}
    </div>
  );
};
